import { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import api from '../../../api'
import { CampaignContext, ProjectContext, TemplateContext } from '../../../contexts'
import { EmailProvider, Template, TemplateUpdateParams } from '../../../types'
import Button, { LinkButton } from '../../../ui/Button'
import { Column, Columns } from '../../../ui/Columns'
import Heading from '../../../ui/Heading'
import Modal from '../../../ui/Modal'
import FormWrapper from '../../../ui/form/FormWrapper'
import { EmailForm, EmailTable } from './email'
import { InAppForm, InAppTable } from './in-app'
import { PushForm, PushTable } from './push'
import { TextForm, TextTable } from './text'
import { WebhookForm, WebhookTable } from './webhook'
import { useResolver } from '../../../hooks'
import Alert from '../../../ui/Alert'
import { InfoTable, Menu, MenuItem } from '../../../ui'
import { languageName } from '../../../utils'
import TemplatePreviewPanel from './TemplatePreviewPanel'
import { EditIcon } from '../../../ui/icons'
import { toast } from 'react-hot-toast/headless'

interface TemplateDetailProps {
    template: Template
}

export default function TemplateDetail({ template }: TemplateDetailProps) {

    const { t } = useTranslation()
    const [{ id, type, data, translation_status }, setTemplate] = useState(template)
    const [campaign, setCampaign] = useContext(CampaignContext)
    const [project] = useContext(ProjectContext)
    const { variants, locales } = useContext(TemplateContext)
    const [isEditOpen, setIsEditOpen] = useState(false)
    const showCodeEditor = type === 'email' || type === 'in_app'
    const [prerender] = useResolver(useCallback(async () => await api.templates.prerender(project.id, id), [data.layout, data]))
    const hasVariations = variants.length > 1 || locales.length > 1
    const isTranslating = translation_status === 'pending'
    const showConvert = data.editor !== 'bee'
    const showConvertHtml = data.editor !== 'code'
    const showRevert = !!data._original_mjml
    const showEmailMenu = showConvert || showConvertHtml || showRevert

    useEffect(() => {
        if (!isTranslating) return
        const interval = setInterval(async () => {
            const updated = await api.templates.get(project.id, id)
            if (updated.translation_status !== 'pending') {
                setTemplate(updated)
                setCampaign(prev => ({
                    ...prev,
                    templates: prev.templates.map(obj => obj.id === id ? updated : obj),
                }))
            }
        }, 2000)
        return () => clearInterval(interval)
    }, [isTranslating, project.id, id, setCampaign])

    async function handleTemplateSave(params: TemplateUpdateParams) {
        const value = await api.templates.update(project.id, id, params)
        setTemplate(value)
        setCampaign(prev => ({
            ...prev,
            templates: prev.templates.map(obj => obj.id === id ? value : obj),
        }))
        setIsEditOpen(false)
    }

    const handleTemplateConvert = async () => {
        try {
            const newTemplate = await api.templates.convert(project.id, template.id)
            setTemplate(newTemplate)
            toast.success(t('template_convert_success'))
        } catch {
            toast.error(t('template_convert_error'))
        }
    }

    const handleTemplateRevert = async () => {
        try {
            const newTemplate = await api.templates.revert(project.id, template.id)
            setTemplate(newTemplate)
            toast.success(t('template_revert_success'))
        } catch {
            toast.error(t('template_revert_error'))
        }
    }

    const handleTemplateConvertHtml = async () => {
        try {
            const newTemplate = await api.templates.convertHtml(project.id, template.id)
            setTemplate(newTemplate)
            toast.success(t('template_convert_success'))
        } catch {
            toast.error(t('template_convert_error'))
        }
    }

    const actions = () => {
        if (type === 'in_app') {
            const isPopup = data.editor === 'popup'
            if (isPopup) {
                return <>
                    <Button size="small" variant="secondary" onClick={() => { setIsEditOpen(true) }}>{t('edit_details')}</Button>
                    <LinkButton size="small" variant="primary" to={`../editor?template=${template.id}`}>{t('edit_popup')}</LinkButton>
                </>
            }

            return <>
                <LinkButton size="small" variant="secondary" to={`../editor?template=${template.id}`}>
                    {t('edit_code')}
                </LinkButton>
                <Button size="small" variant="primary" onClick={() => { setIsEditOpen(true) }}>{t('edit_details')}</Button>
            </>
        }

        if (type === 'email') {
            return <>
                <Button size="small" variant="secondary" onClick={() => { setIsEditOpen(true) }}>{t('edit_details')}</Button>
                <LinkButton size="small" variant="primary" to={`../editor?template=${template.id}`}>{t('edit_design')}</LinkButton>
                {showEmailMenu && (
                    <Menu size="small">
                        {showConvert && (
                            <MenuItem icon={<EditIcon />} onClick={async () => await handleTemplateConvert()}>
                                {t('template_convert_to_new_editor')}
                            </MenuItem>
                        )}
                        {showConvertHtml && (
                            <MenuItem icon={<EditIcon />} onClick={async () => await handleTemplateConvertHtml()}>
                                {t('template_convert_to_html')}
                            </MenuItem>
                        )}
                        {showRevert && (
                            <MenuItem icon={<EditIcon />} onClick={async () => await handleTemplateRevert()}>
                                {t('template_revert_to_original')}
                            </MenuItem>
                        )}
                    </Menu>
                )}
            </>
        }

        return (
            <Button size="small" variant="secondary" onClick={() => { setIsEditOpen(true) }}>{t('edit_details')}</Button>
        )
    }

    return (
        <>
            {isTranslating && (
                <Alert variant="info" title={<><span className="alert-spinner" />{t('translation_pending')}</>} size="small" />
            )}
            {translation_status === 'error' && (
                <Alert variant="error" title={t('translation_error')} size="small" />
            )}
            <Columns>
                <Column>
                    <Heading title={t('details')} size="h4" actions={
                        campaign.state !== 'finished' && !isTranslating && actions()
                    } />

                    {(hasVariations && template)
                    && <InfoTable rows={{
                        [t('locale')]: `${languageName(template.locale)} (${template.locale})`,
                        ...variants.length > 1 ? { [t('variant')]: t('variant_detail', { name: template.name, count: variants.length }) } : {},
                    }} />
                    }

                    {
                        {
                            email: <EmailTable
                                data={data}
                                subscription={campaign.subscription}
                                provider={campaign.provider as EmailProvider}
                            />,
                            text: <TextTable data={data} locale={template.locale} />,
                            push: <PushTable data={data} />,
                            webhook: <WebhookTable data={data} />,
                            in_app: <InAppTable data={data} />,
                        }[type]
                    }
                </Column>

                <TemplatePreviewPanel
                    templateId={id}
                    type={type}
                    data={data}
                    prerenderData={prerender?.data}
                    locale={template.locale}
                    showCodeEditor={showCodeEditor}
                />
            </Columns>

            <Modal title={t('edit_template_details')}
                open={isEditOpen}
                onClose={() => setIsEditOpen(false)}
                size={(type === 'in_app' || (type === 'text' && data.editor === 'interactive')) ? 'regular' : 'small'}
            >
                <FormWrapper<TemplateUpdateParams>
                    onSubmit={handleTemplateSave}
                    defaultValues={{ data }}
                    submitLabel={t('save')}
                >
                    {form => <>
                        {
                            {
                                email: <EmailForm form={form} />,
                                text: <TextForm form={form} />,
                                push: <PushForm form={form} />,
                                webhook: <WebhookForm form={form} />,
                                in_app: <InAppForm form={form} />,
                            }[type]
                        }
                    </>}
                </FormWrapper>
            </Modal>
        </>
    )
}
